import type { Setting } from '@isoftdata/svelte-setting'
import type { ComponentProps } from 'svelte'
import svelteComponent from './Settings.svelte' // Svelte component

import type { AppContext } from 'types/common'
import { graphql } from '$houdini'
import showErrorAndRedirect from 'utility/show-error-and-redirect'
import { stringToBoolean } from '@isoftdata/utility-string'

export default function ({ stateRouter, mediator, i18next: { t: translate } }: AppContext) {
	stateRouter.addState({
		name: 'app.configuration.settings',
		route: 'settings',
		querystringParameters: ['lastResetTime', 'lastSavedTime'],
		defaultParameters: {
			lastResetTime: null,
			lastSavedTime: null,
			//	selectedCategory: translate('common:all', 'All'),
			selectedCategory: () => translate('settings.categories.all', 'All'),
		},
		template: {
			svelte: true,
			component: svelteComponent,
			options: {},
		},
		canLeaveState(domApi) {
			// eslint-disable-next-line @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-call
			return domApi.canLeaveState()
		},
		async resolve(_data, parameters): Promise<Omit<ComponentProps<typeof svelteComponent>, 'asr'>> {
			const { selectedCategory } = parameters

			const { data } = await settingsData.fetch({})

			if (!data) {
				throw showErrorAndRedirect(
					mediator,
					translate('configuration.settings.failedToLoadSettingsData', 'Failed to load settings data'),
					translate('configuration.settings.noDataReturnedFromServer', 'No data returned from server'),
					{
						name: 'app.configuration',
					},
				)
			}

			return {
				selectedCategory: selectedCategory ?? translate('settings.categories.all', 'All'),
				settings: data.settingValuesForConfig.reduce((acc: Array<Setting>, setting) => {
					if (setting.scope === 'GLOBAL') {
						acc.push({
							...setting,
							description: setting.description ?? '',
							dataType: checkSettingValue(setting.value),
							type: transformTypeEnum(setting.type),
							readOnly: !data.session.permissions.SETTINGS_GLOBAL_ADMINISTRATOR,
						})
					} else if (setting.scope === 'STORE') {
						acc.push({
							...setting,
							description: setting.description ?? '',
							dataType: checkSettingValue(setting.value),
							type: transformTypeEnum(setting.type),
							readOnly: !data.session.permissions.SETTINGS_STORE_ADMINISTRATOR,
						})
					} else if (setting.scope === 'USER') {
						acc.push({
							...setting,
							description: setting.description ?? '',
							dataType: checkSettingValue(setting.value),
							type: transformTypeEnum(setting.type),
						})
					}

					return acc
				}, []),
				settingTypeFilters: [
					{
						name: translate('settings:importantConfiguration', 'Important Configuration'),
						//name: 'Important Configuration',
						value: stringToBoolean(data.settingValues.settingsConfig.importantConfigurationShown ?? 'True'),
						realSettingName: "Configure Settings Dialog: Show 'important configuration' settings",
					},
					{
						name: translate('settings:optionalConfiguration', 'Optional Configuration'),
						//name: 'Optional Configuration',
						value: stringToBoolean(data.settingValues.settingsConfig.optionalConfigurationShown ?? 'True'),
						realSettingName: "Configure Settings Dialog: Show 'optional configuration' settings",
					},
					{
						name: translate('settings:preference', 'Preference'),
						//name: 'Preference',
						value: stringToBoolean(data.settingValues.settingsConfig.preferenceConfigurationShown ?? 'True'),
						realSettingName: "Configure Settings Dialog: Show 'preference' settings",
					},
					{
						name: translate('settings:interfacePreference', 'Interface Preference'),
						//name: 'Interface Preference',
						value: stringToBoolean(data.settingValues.settingsConfig.interfacePreferenceConfigurationShown ?? 'True'),
						realSettingName: "Configure Settings Dialog: Show 'interface preference' settings",
					},
					{
						name: translate('settings:interfaceHistory', 'Interface History'),
						//name: 'Interface History',
						value: stringToBoolean(data.settingValues.settingsConfig.interfaceHistoryConfigurationShown ?? 'True'),
						realSettingName: "Configure Settings Dialog: Show 'interface history' settings",
					},
				],
			}
		},
	})
}

function transformTypeEnum(settingType: string) {
	// This is to convert in and out of the API to what the UI expects
	switch (settingType) {
		case 'IMPORTANT_CONFIGURATION':
			return 'Important Configuration'
		case 'INTERFACE_HISTORY':
			return 'Interface History'
		case 'INTERFACE_PREFERENCE':
			return 'Interface Preference'
		case 'OPTIONAL_CONFIGURATION':
			return 'Optional Configuration'
		case 'PREFERENCE':
			return 'Preference'
		case 'ACCESSIBILITY':
			return 'Accessibility'
		default:
			return 'Unknown'
	}
}

function checkSettingValue(settingValue: string | null) {
	// This is an abstraction to check the type of the setting value for display
	const numberRegex = /^\d+$/
	const booleanRegex = /^(True|False)$/
	if (settingValue && numberRegex.test(settingValue)) {
		return 'number'
	} else if (settingValue && booleanRegex.test(settingValue)) {
		return 'boolean'
	}
	return 'string'
}

const settingsData = graphql(`
	query SettingsData {
		session {
			permissions {
				SETTINGS_GLOBAL_ADMINISTRATOR
				SETTINGS_STORE_ADMINISTRATOR
				#SETTINGS_GROUP_ADMINISTRATOR
			}
		}
		settingValues {
			settingsConfig {
				importantConfigurationShown
				interfaceHistoryConfigurationShown
				interfacePreferenceConfigurationShown
				optionalConfigurationShown
				preferenceConfigurationShown
			}
		}
		settingValuesForConfig {
			id
			defaultValue
			description
			category: location
			name
			scope
			type: settingType
			value
		}
	}
`)
